<template>
    <div class="grid grid-cols-1 gap-4 sm:grid-cols-2 lg:grid-cols-4">
        <div
            v-for="card in cards"
            :key="card.label"
            class="rounded-xl border border-gray-200 bg-white p-5"
        >
            <p
                class="text-xs font-medium uppercase tracking-wide text-gray-400"
            >
                {{ card.label }}
            </p>
            <p class="mt-1 text-2xl font-semibold text-gray-800">
                {{ card.value }}
            </p>
            <p v-if="card.hint" class="mt-1 text-xs text-gray-400">
                {{ card.hint }}
            </p>
        </div>
    </div>
</template>

<script lang="ts" setup>
import { computed } from "vue";
import { useTranslations } from "@/composables/useTranslations";
import { formatCount, formatDuration, formatUsd } from "../formatters";
import type { IAiUsageMetrics } from "../types";

interface IMetricCard {
    label: string;
    value: string;
    hint: string | null;
}

const props = defineProps<{
    metrics: IAiUsageMetrics;
}>();

const { t } = useTranslations();

const cards = computed((): IMetricCard[] => [
    {
        label: t("vue.kiosk_ai_usage_chats"),
        value: formatCount(props.metrics.chat_count),
        hint: null,
    },
    {
        label: t("vue.kiosk_ai_usage_messages"),
        value: formatCount(props.metrics.message_count),
        hint: `${formatCount(props.metrics.answer_count)} ${t(
            "vue.kiosk_ai_usage_answers",
        )}`,
    },
    {
        label: t("vue.kiosk_ai_usage_jobs"),
        value: formatCount(props.metrics.job_count),
        hint: `${formatCount(props.metrics.failed_job_count)} ${t(
            "vue.kiosk_ai_usage_failed_jobs",
        )}`,
    },
    {
        label: t("vue.kiosk_ai_usage_avg_response"),
        value: formatDuration(props.metrics.average_response_ms),
        hint: null,
    },
    {
        label: t("vue.kiosk_ai_usage_total_cost"),
        value: formatUsd(props.metrics.total_usd),
        hint: `${formatCount(props.metrics.priced_answer_count)} ${t(
            "vue.kiosk_ai_usage_priced_answers",
        )}`,
    },
    {
        label: t("vue.kiosk_ai_usage_avg_cost_per_answer"),
        value: formatUsd(props.metrics.average_usd_per_answer),
        hint: null,
    },
    {
        label: t("vue.kiosk_ai_usage_avg_cost_per_chat"),
        value: formatUsd(props.metrics.average_usd_per_chat),
        hint: null,
    },
]);
</script>
